import type { OutgoingStatisticsResponse } from '../../../store/reducers/OutgoingStatisticsSlice';

import {
  ATTEMPT_DISTRIBUTION_SECTION,
  buildAttemptDistributionCallRows,
  buildAttemptDistributionTaskRows,
} from './attemptDistributionRows';
import { buildCommonRows } from './commonRows';
import {
  buildEfficiencyCallRows,
  buildEfficiencyTaskRows,
} from './efficiencyRows';
import {
  buildFailedReasonCallRows,
  buildFailedReasonTaskRows,
} from './failedReasonRows';
import { buildInteractionSection } from './interactionRows';
import {
  buildOutgoingStatisticMetrics,
  type OutgoingStatisticMetrics,
} from './outgoingStatisticMetrics';
import { STATISTIC_MODE_OPTIONS } from './outgoingStatisticModeMetadata';
import { createSectionRow } from './statisticRowFactory';
import type {
  OutgoingStatisticRow,
  StatisticDisplayMode,
} from './statisticRowTypes';

export type { OutgoingStatisticRow, StatisticDisplayMode };

type RowBuilder = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
) => OutgoingStatisticRow[];

const createModeSection = ({
  key,
  section,
  statistic,
  metrics,
  buildTasks,
  buildCalls,
}: {
  readonly key: string;
  readonly section: string;
  readonly statistic: OutgoingStatisticsResponse;
  readonly metrics: OutgoingStatisticMetrics;
  readonly buildTasks: RowBuilder;
  readonly buildCalls: RowBuilder;
}): OutgoingStatisticRow => {
  const childrenByMode: Record<StatisticDisplayMode, OutgoingStatisticRow[]> = {
    tasks: buildTasks(statistic, metrics),
    calls: buildCalls(statistic, metrics),
  };

  return createSectionRow({
    key,
    section,
    children: childrenByMode.tasks,
    childrenByMode,
    modeOptions: STATISTIC_MODE_OPTIONS,
    defaultMode: 'tasks',
  });
};

export const buildOutgoingStatisticRows = (
  statistic: OutgoingStatisticsResponse,
  selectedScenario: string | null,
): OutgoingStatisticRow[] => {
  const metrics = buildOutgoingStatisticMetrics(statistic);
  const rows: OutgoingStatisticRow[] = [
    createSectionRow({
      key: 'common',
      section: 'Общие показатели',
      children: buildCommonRows(statistic, metrics),
    }),
    createModeSection({
      key: 'efficiency',
      section: 'Эффективность обзвона',
      statistic,
      metrics,
      buildTasks: buildEfficiencyTaskRows,
      buildCalls: buildEfficiencyCallRows,
    }),
  ];
  const interactionRows = buildInteractionSection(
    statistic,
    metrics,
    selectedScenario,
  );

  if (interactionRows) {
    rows.push(interactionRows);
  }

  rows.push(
    createModeSection({
      key: 'attemptDistribution',
      section: ATTEMPT_DISTRIBUTION_SECTION,
      statistic,
      metrics,
      buildTasks: buildAttemptDistributionTaskRows,
      buildCalls: buildAttemptDistributionCallRows,
    }),
    createModeSection({
      key: 'failedReasons',
      section: 'Причины отсутствия целевого результата',
      statistic,
      metrics,
      buildTasks: buildFailedReasonTaskRows,
      buildCalls: buildFailedReasonCallRows,
    }),
  );

  return rows;
};
